import { Routes } from '@angular/router';
import { SigninPage } from './pages/signin/signin.component';
import { ResetpasswordPage } from './pages/resetpassword/resetpassword.component';
import { PortalComponent as OrganizationPortal } from './pages/organization/portal/portal.component';
import { ContactsComponent } from './pages/organization/contacts/contacts.component';
import { MessagesComponent } from './pages/organization/messages/messages.component';
import { CampaignsComponent } from './pages/organization/campaigns/campaigns.component';
import { SettingsComponent } from './pages/organization/settings/settings.component';
import { DashboardComponent } from './pages/organization/dashboard/dashboard.component';
import { ContactListComponent } from './pages/organization/contacts/contact-list/contact-list.component';
import { OrganizationListComponent } from './pages/organization/contacts/organization-list/organization-list.component';
import { ListSegmentedComponent } from './pages/organization/contacts/segmented-list/list-segment/list-segment.component';
import { CreateCampaignComponent } from './pages/organization/campaigns/create-campaign/create-campaign.component';
import { MultipleContactsComponent } from './pages/organization/contacts/multiple-contacts/multiple-contacts.component';
import { OrganizationsComponent } from './pages/organization/organizations/organizations.component';
import { OrganizationDetailsComponent } from './pages/organization/organizations/organization-details/organization-details.component';
import { SegmentedListComponent } from './pages/organization/contacts/segmented-list/segmented-list.component';
import { CreateSegmentComponent } from './pages/organization/contacts/segmented-list/create-segment/create-segment.component';
import { TagsComponent } from './pages/organization/contacts/tags/tags.component';
import { TagListComponent } from './pages/organization/contacts/tags/tag-list/tag-list.component';
import { TagDetailsComponent } from './pages/organization/contacts/tags/tag-details/tag-details.component';
import { CampaignDetailsComponent } from './pages/organization/campaigns/campaign-details/campaign-details.component';
import { OverviewComponent } from './pages/organization/campaigns/campaign-details/overview/overview.component';
import { MessageContentComponent } from './pages/organization/campaigns/campaign-details/message-content/message-content.component';
import { RecipientsComponent } from './pages/organization/campaigns/campaign-details/recipients/recipients.component';
import { ReportsComponent } from './pages/organization/campaigns/campaign-details/reports/reports.component';
import { SegmentDetailsComponent } from './pages/organization/contacts/segmented-list/segment-details/segment-details.component';
import { CsvComponent } from './pages/organization/contacts/csv/csv.component';
import { RouterModule } from '@angular/router';

// ROUTER GUARD FUNCTIONS
import { canActivateChildAuthGuard, canActivateGuard, redirectSignedInGuard } from './services/routing/routeGuards';
import { MessageRecipient } from './pages/organization/messages/select-message-recipient/message-recipient.component';
import { NotfoundComponent } from './pages/notfound/notfound.component';
import { ProfileSettingsComponent } from './pages/organization/settings/profile-settings/profile-settings.component';
import { OrganizationSettingsComponent } from './pages/organization/settings/organization-settings/organization-settings.component';
import { UserManagementSettingsComponent } from './pages/organization/settings/user-management-settings/user-management-settings.component';
import { NotificationsSettingsComponent } from './pages/organization/settings/notifications-settings/notifications-settings.component';
import { TestContactsComponent } from './pages/organization/settings/test-contacts/test-contacts.component';
import { MessagePreview } from './pages/organization/messages/message-preview/message-preview.component';
import { NgModule } from '@angular/core';
import { MessageDetailsView } from './pages/organization/messages/message-details-view/message-details-view';
import { MessageView } from './pages/organization/messages/message-view/message-view';
import { MessageTemplates } from './pages/organization/messages/message-templates/message-templates';
import { MainReportComponent } from './pages/organization/main-report/main-report.component';
import { MessagingReportsComponent } from './pages/organization/messaging-reports/messaging-reports.component';
import { PaypointReportsComponent } from './pages/organization/paypoint-reports/paypoint-reports.component';
import { CampaignsListComponent } from './pages/organization/campaigns/campaign-list/campaigns-list.component';
import { ReportOverviewComponent } from './pages/organization/campaigns/campaign-details/reports/report-overview/report-overview.component';
import { ReportMessageDetailsComponent } from './pages/organization/campaigns/campaign-details/reports/messages-report/report-message-details/report-message-details.component';
import { MessageReportsComponent } from './pages/organization/messages/message-reports/message-reports.component';
import { MessagesReportComponent } from './pages/organization/campaigns/campaign-details/reports/messages-report/messages-report.component';
import { ReportMessageContactDetailsComponent } from './pages/organization/campaigns/campaign-details/reports/messages-report/report-message-contact-details/report-message-contact-details.component';
import { EditSegmentComponent } from './pages/organization/contacts/segmented-list/edit-segment/edit-segment.component';
import { CreateNewMessageComponent } from './pages/organization/messages/create-new-message/create-new-message.component';
import { NewpasswordComponent } from './pages/newpassword/newpassword.component';
import { UssdSimulatorComponent } from './pages/organization/ussd-simulator/ussd-simulator.component';
import { SimulatorComponent } from './pages/organization/ussd-simulator/simulator/simulator.component';
import { UssdApplicationsComponent } from './pages/organization/ussd-simulator/applications/applications.component';
import { UssdReportsComponent } from './pages/organization/ussd-simulator/reports/reports.component';
import { UssdApplicationDetailComponent } from './pages/organization/ussd-simulator/applications/application-detail/application-detail.component';
import { SmartBroadcastComponent } from './pages/organization/smart-broadcast/smart-broadcast.component';
import { CreateApplicationComponent } from './pages/organization/ussd-simulator/applications/create-application/create-application.component';
import { EditApplicationComponent } from './pages/organization/ussd-simulator/applications/edit-application/edit-application.component';

import { smartBroadcastLeaveGuard } from './pages/organization/smart-broadcast/smart-broadcast-leave.guard';
import { apiTrafficGuard } from './services/routing/report-access.guard';

export const routes: Routes = [
  {
    path: '',
    title: 'Sign in · Black Konnect',
    component: SigninPage,
    canActivate: [redirectSignedInGuard],
  },
  // Both pages were unfinished stubs (they echoed passwords back in an
  // alert) and nothing links to them. Keep the URLs working: setting a
  // password happens on the emailed /new-password link, changing it in
  // Settings > Profile.
  { path: 'set-password', redirectTo: 'new-password', pathMatch: 'full' },
  { path: 'change-password', redirectTo: 'organization/settings/profile', pathMatch: 'full' },
  {
    path: 'reset-password',
    title: 'Reset password · Black Konnect',
    component: ResetpasswordPage,
  },
  {
    path: 'new-password',
    title: 'Set a new password · Black Konnect',
    component: NewpasswordComponent,
  },
  {
    path: 'organization',
    title: 'Black Konnect',
    canActivate: [canActivateGuard],
    canActivateChild: [canActivateChildAuthGuard],
    // data: { breadcrumb: 'Organization' },
    component: OrganizationPortal,
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        title: 'Dashboard · Black Konnect',
        component: DashboardComponent,
        data: { breadcrumb: 'Dashboard' },
      },
      {
        path: 'contacts',
        title: 'Contacts · Black Konnect',
        component: ContactsComponent,
        data: { breadcrumb: 'Contacts' },
        children: [
          {
            path: '',
            component: ContactListComponent,
            data: { breadcrumb: 'Contact list' },
          },
          {
            path: 'multiple-contacts',
            title: 'Add contacts · Black Konnect',
            component: MultipleContactsComponent,
            data: { breadcrumb: 'Multiple contacts' },
          },
          {
            path: 'csv',
            title: 'Import contacts · Black Konnect',
            component: CsvComponent,
            data: { breadcrumb: 'CSV import' },
          },
          {
            path: 'organizations',
            component: OrganizationsComponent,
            title: 'Organizations · Black Konnect',
            // data: { breadcrumb: 'Organizations' },
            children: [
              {
                path: '',
                component: OrganizationListComponent,
                data: { breadcrumb: 'Organization list' },
              },
              {
                path: ':id',
                title: 'Organization details · Black Konnect',
                component: OrganizationDetailsComponent,
                data: { breadcrumb: 'Organization details' },
              },
            ],
          },
          {
            path: 'tags',
            title: 'Tags · Black Konnect',
            component: TagsComponent,
            // data: { breadcrumb: 'Tags' },
            children: [
              {
                path: '',
                component: TagListComponent,
                data: { breadcrumb: 'Tag list' },
              },
              {
                path: ':id',
                title: 'Tag details · Black Konnect',
                component: TagDetailsComponent,
                data: { breadcrumb: 'Tag details' },
              },
            ],
          },
          {
            path: 'segmented-list',
            title: 'Segments · Black Konnect',
            component: SegmentedListComponent,
            // data: { breadcrumb: 'Segmented list' },
            children: [
              {
                path: '',
                component: ListSegmentedComponent,
                data: { breadcrumb: 'Segmented list' },
              },
              {
                path: 'create',
                title: 'Create segment · Black Konnect',
                component: CreateSegmentComponent,
                data: { breadcrumb: 'Create segment' },
              },
              {
                path: 'edit/:id',
                title: 'Edit segment · Black Konnect',
                component: EditSegmentComponent,
                data: { breadcrumb: 'Edit segment' },
              },
              {
                path: ':id',
                title: 'Segment details · Black Konnect',
                component: SegmentDetailsComponent,
                data: { breadcrumb: 'Segment details' },
              },
            ],
          },
        ],
      },
      {
        path: 'messages',
        title: 'Messages · Black Konnect',
        component: MessageView,
        data: { breadcrumb: 'Messages' },
        children: [
          {
            path: '',
            component: MessagesComponent,
            data: { breadcrumb: 'Message list' },
          },
          {
            path: 'templates/new',
            title: 'New template · Black Konnect',
            component: MessageTemplates,
            data: { breadcrumb: 'Templates' },
          },

          {
            path: 'new',
            title: 'New message · Black Konnect',
            component: MessageRecipient,
            data: { breadcrumb: 'New message' },
          },
          {
            path: 'create',
            title: 'New message · Black Konnect',
            component: CreateNewMessageComponent,
            data: { breadcrumb: 'New message' },
          },
          {
            path: 'preview',
            title: 'Preview message · Black Konnect',
            component: MessagePreview,
            data: { breadcrumb: 'Preview message' },
          },
          // Old links to the removed broadcast page land on the message list
          // instead of being read as a message id.
          {
            path: 'broadcast',
            redirectTo: '',
            pathMatch: 'full',
          },
          {
            path: ':id',
            title: 'Message details · Black Konnect',
            component: MessageDetailsView,
            data: { breadcrumb: 'Message details' },
          },
          {
            path: 'report/:id',
            title: 'Message report · Black Konnect',
            component: MessageReportsComponent,
            data: { breadcrumb: 'Message reports' },
          },
        ],
      },
      // smart broadcast (personalized bulk SMS from a data file)
      {
        path: 'smart-broadcast',
        title: 'Custom SMS · Black Konnect',
        component: SmartBroadcastComponent,
        canDeactivate: [smartBroadcastLeaveGuard],
        data: { breadcrumb: 'Custom SMS' },
      },
      // reports page
      {
        path: 'reports',
        title: 'Delivery reports · Black Konnect',
        component: MessagingReportsComponent,
        data: { breadcrumb: 'Delivery reports' },
      },
      {
        path: 'smsc-reports',
        title: 'API traffic · Black Konnect',
        component: MainReportComponent,
        // Same roles as the sidebar's "API traffic" link.
        canActivate: [apiTrafficGuard],
        data: { breadcrumb: 'API traffic' },
      },
      // paypoint bill-payment reports (Metabase embed, org-locked)
      {
        path: 'paypoint-reports',
        component: PaypointReportsComponent,
        data: { breadcrumb: 'Bill Payment Reports' },
      },

      // campaigns
      {
        path: 'campaigns',
        title: 'Campaigns · Black Konnect',
        component: CampaignsComponent,
        data: { breadcrumb: 'Campaigns' },
        children: [
          {
            path: '',
            component: CampaignsListComponent,
          },
          {
            path: 'create-campaign',
            title: 'Create campaign · Black Konnect',
            component: CreateCampaignComponent,
            data: { breadcrumb: 'Create campaign' },
          },
          {
            path: ':id/:uuid',
            title: 'Campaign · Black Konnect',
            component: CampaignDetailsComponent,
            // data: { breadcrumb: 'Campaign Details' },
            children: [
              {
                path: 'overview',
                component: OverviewComponent,
                data: { breadcrumb: 'Overview' },
              },
              {
                path: 'message-content',
                component: MessageContentComponent,
                data: { breadcrumb: 'Message content' },
              },
              {
                path: 'recipients',
                component: RecipientsComponent,
                data: { breadcrumb: 'Recipients' },
              },
              {
                path: 'reports',
                component: ReportsComponent,
                data: { breadcrumb: 'Reports' },
                children: [
                  {
                    path: '',
                    component: ReportOverviewComponent,
                  },
                  {
                    path: ':messageId/message-report',
                    component: MessagesReportComponent,
                    children: [
                      {
                        path: '',
                        component: ReportMessageDetailsComponent,
                        data: { breadcrumb: 'Message report' },
                      },
                      {
                        path: 'contact-details',
                        component: ReportMessageContactDetailsComponent,
                        data: { breadcrumb: 'Contact details' },
                      },
                    ],
                  },
                ],
              },
              { path: '', redirectTo: 'overview', pathMatch: 'full' },
            ],
          },
        ],
      },

      // settings
      {
        path: 'settings',
        title: 'Settings · Black Konnect',
        component: SettingsComponent,
        data: { breadcrumb: 'Settings' },
        children: [
          {
            path: 'profile',
            title: 'Profile · Black Konnect',
            component: ProfileSettingsComponent,
            data: { breadcrumb: 'Profile' },
          },
          {
            path: 'organization',
            title: 'Organization settings · Black Konnect',
            component: OrganizationSettingsComponent,
            data: { breadcrumb: 'Organization settings' },
          },
          {
            path: 'user-management',
            title: 'User management · Black Konnect',
            component: UserManagementSettingsComponent,
            data: { breadcrumb: 'User management' },
          },
          {
            path: 'notifications',
            title: 'Notifications · Black Konnect',
            component: NotificationsSettingsComponent,
            data: { breadcrumb: 'Notifications' },
          },
          {
            path: 'test-contacts',
            title: 'Test contacts · Black Konnect',
            component: TestContactsComponent,
            data: { breadcrumb: 'Test contacts' },
          },
          { path: '', redirectTo: 'profile', pathMatch: 'full' },
        ],
      },

      // ussd simulator
      {
        path: 'ussd-simulator',
        title: 'USSD · Black Konnect',
        component: UssdSimulatorComponent,
        data: { breadcrumb: 'USSD simulator' },
        children: [
          {
            path: 'simulator',
            title: 'USSD simulator · Black Konnect',
            component: SimulatorComponent,
            data: { breadcrumb: 'Simulator' },
          },
          {
            path: 'applications',
            title: 'USSD applications · Black Konnect',
            component: UssdApplicationsComponent,
            data: { breadcrumb: 'Applications' },
          },
          {
            path: 'applications/new',
            title: 'Create USSD application · Black Konnect',
            component: CreateApplicationComponent,
            data: { breadcrumb: 'Create application' },
          },
          {
            path: 'applications/:id/edit',
            title: 'Edit USSD application · Black Konnect',
            component: EditApplicationComponent,
            data: { breadcrumb: 'Edit application' },
          },
          {
            path: 'applications/:id',
            title: 'USSD application · Black Konnect',
            component: UssdApplicationDetailComponent,
            data: { breadcrumb: 'Application details' },
          },
          {
            path: 'reports',
            title: 'USSD reports · Black Konnect',
            component: UssdReportsComponent,
            data: { breadcrumb: 'Reports' },
          },
          { path: '', redirectTo: 'simulator', pathMatch: 'full' },
        ],
      },

      // not found
      {
        path: '**',
        title: 'Page not found · Black Konnect',
        component: NotfoundComponent,
        data: { breadcrumb: 'Not found' },
      },
    ],
  },

  {
    path: '**',
    title: 'Page not found · Black Konnect',
    component: NotfoundComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled', // Automatically scrolls to the top
      anchorScrolling: 'enabled', // Enables anchor links
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
